import { useEffect, useState } from "react";
import { BigNumber } from "bignumber.js";
import { utils as koilibUtils } from "koilib";
import { get as _get } from "lodash";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useWallet } from "../hooks/useWallet";
import { InitializeSwaps } from "../helpers/initialize";
import { initializeSwaps as InitializeSwapsRD } from "../redux/actions/swap";

// utils
import { maxDecimal, fetcherToken } from "../utils/token";
import { waitTransation, describeWaitError } from "../utils/transactions";

// components mui

// icons

// components
import Maintenance from "../components/Maintenance";
import TokenInputPanel from "../components/TokenInputPanel";
import SwapRoute from "../components/SwapRoute";
import DetailRow from "../components/DetailRow";
import TxSnackbarMessage from "../components/TxSnackbarMessage";
import { formatAmount } from "../utils/format";
import { getErrorMessage } from "../utils/errors";

// Actions
import { setModal, setModalData } from "../redux/actions/modals";
import { setTokenReceived, setTokenSend } from "../redux/actions/swap";

// helpers
import { calculateDefaultMana } from "../helpers/calculate";
import { fetchRoutePairs } from "../helpers/pairs";
import { invalidateAfterTransaction } from "../helpers/queryClient";
import { useCommonPairs } from "../hooks/queries";

// routing
import {
  findRoutes,
  findBestTradeExactIn,
  findBestTradeExactOut,
  computePriceImpact,
  quoteRoute,
} from "../utils/routing";

// constants
import { CONFIG_BASE } from "../constants/configs";

// contracts
import { PeripheryContract, TokenContract, NameServiceContract } from "../helpers/contracts";

const SwapPage = () => {
  // Dispatch to call actions
  const dispatch = useDispatch();
  const Snackbar = useSnackbar();

  // selectors
  const swapSelector = useSelector((state) => state.swap);
  const walletSelector = useWallet();
  const settingsSelector = useSelector((state) => state.settings);
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);

  // state
  const [balanceSend, setBalanceSend] = useState("loading");
  const [, setBalanceReceived] = useState("loading");
  const [inputSend, setInputSend] = useState(0);
  const [inputReceived, setInputReceived] = useState(0);
  const [debounceIn, setDebounceIn] = useState(null);
  const [debounceOut, setDebounceOut] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("0");
  const [priceImpact, setPriceImpact] = useState("0");

  const [loading, setLoading] = useState(false);
  const [typeSwap, setTypeSwap] = useState(null);
  const [routerSwap, setRouterSwap] = useState([]);
  const [routerErrors, setRouterErrors] = useState(null);
  const [swapPath, setSwapPath] = useState(null);

  // variables
  const tokenSend = _get(swapSelector, "send", null);
  const tokenReceived = _get(swapSelector, "received", null);

  // pairs and reserves of the possible routes, refreshed every 30 s (hooks/queries.js)
  const pairsQuery = useCommonPairs(tokenSend, tokenReceived);
  const reserve = pairsQuery.data || null;
  const slippage = _get(settingsSelector, "slippage", "0.1");
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);

  // constants
  const debounceTiming = 800;

  const openModalSelectToken = (data) => {
    dispatch(setModalData(data));
    dispatch(setModal("SelectToken"));
  };
  const openModalSettingsApp = () => {
    dispatch(setModalData(null));
    dispatch(setModal("SettingsApp"));
  };
  const swichTokens = () => {
    dispatch(setTokenSend(tokenReceived));
    dispatch(setTokenReceived(tokenSend));
  };
  // adds the token list logos to the tokens of a route
  const withLogos = (path) => {
    let tokenList = base_tokens.concat(custom_tokens);
    return path.map((token) => {
      let tokenInfo = tokenList.find((t) => t.address == _get(token, "address", null));
      if (tokenInfo) {
        token.logoURI = _get(tokenInfo, "logoURI", null);
      }
      return token;
    });
  };
  // no trade: either no route links both tokens, or the routes lack liquidity for the amount
  const noTradeError = (_tokenSend, _tokenReceived) =>
    findRoutes(reserve, _tokenSend, _tokenReceived).length ? "Finite" : "NotRoute";
  const clearTrade = () => {
    setPriceImpact("0");
    setExchangeRate("");
    setSwapPath(null);
    setRouterSwap([]);
  };

  const amountsIn = async (amountPrev) => {
    let amount = maxDecimal(amountPrev, tokenSend.decimals);
    let _amountIn = koilibUtils.parseUnits(amount, tokenSend.decimals);
    setRouterErrors(null);
    if (tokenReceived) {
      try {
        let _tokenSend = await fetcherToken(CONFIG_BASE.chain.id, tokenSend);
        let _tokenReceived = await fetcherToken(CONFIG_BASE.chain.id, tokenReceived);
        let traderesult = findBestTradeExactIn(reserve, _tokenSend, _tokenReceived, _amountIn);
        if (traderesult) {
          let path = withLogos(traderesult.path);
          let result = traderesult.amount_out_min.toFixed(0);
          let _amountF =
            tokenReceived.decimals != "0" ? koilibUtils.formatUnits(result, tokenReceived.decimals) : result;
          setInputReceived(_amountF);
          setPriceImpact(computePriceImpact(traderesult.pairs, path, _amountIn, result));
          setExchangeRate(new BigNumber(_amountF).div(amount).toFixed(Number(tokenReceived.decimals)));
          setSwapPath(path);
          setRouterSwap(path);
        } else {
          setRouterErrors(noTradeError(_tokenSend, _tokenReceived));
          setInputReceived("");
          clearTrade();
        }
      } catch (error) {
        setRouterErrors(error);
        console.log(error);
        setInputReceived("");
        clearTrade();
      }
    }
    setLoading(false);
  };
  const amountsOut = async (amountPrev) => {
    let amount = maxDecimal(amountPrev, tokenReceived.decimals);
    let _amountOut = koilibUtils.parseUnits(amount, tokenReceived.decimals);
    setRouterErrors(null);
    if (tokenSend) {
      try {
        let _tokenSend = await fetcherToken(CONFIG_BASE.chain.id, tokenSend);
        let _tokenReceived = await fetcherToken(CONFIG_BASE.chain.id, tokenReceived);
        let traderesult = findBestTradeExactOut(reserve, _tokenSend, _tokenReceived, _amountOut);
        if (traderesult) {
          let path = withLogos(traderesult.path);
          let result = traderesult.amount_in_max.toFixed(0);
          let _amountF = tokenSend.decimals != "0" ? koilibUtils.formatUnits(result, tokenSend.decimals) : result;
          setInputSend(_amountF);
          setPriceImpact(computePriceImpact(traderesult.pairs, path, result, _amountOut));
          setExchangeRate(new BigNumber(amount).div(_amountF).toFixed(Number(tokenSend.decimals)));
          setSwapPath(path);
          setRouterSwap(path);
        } else {
          setRouterErrors(noTradeError(_tokenSend, _tokenReceived));
          setInputSend("");
          clearTrade();
        }
      } catch (error) {
        setRouterErrors(error);
        console.log(error);
        setInputSend("");
        clearTrade();
      }
    }
    setLoading(false);
  };
  const cleanState = () => {
    setTypeSwap(null);
    setInputSend("");
    setPriceImpact("0");
    setExchangeRate("0");
    setInputReceived("");
    setSwapPath(null);
    setRouterSwap([]);
  };
  const changeReserves = () => {
    if (typeSwap != null && reserve && Object.keys(reserve).length != 0) {
      if (typeSwap == "SWAP-IN" && inputSend) amountsIn(inputSend);
      if (typeSwap == "SWAP-OUT" && inputReceived) amountsOut(inputReceived);
    }
  };
  const debounceFnIn = (value) => {
    setLoading(true);
    setInputSend(value);
    setTypeSwap("SWAP-IN");
    if (debounceIn) clearTimeout(debounceIn);
    let _submit = () => amountsIn(value);
    let fncDelayed = setTimeout(_submit, debounceTiming);
    setDebounceIn(fncDelayed);
  };
  const debounceFnOut = (value) => {
    setLoading(true);
    setInputReceived(value);
    setTypeSwap("SWAP-OUT");
    if (debounceOut) clearTimeout(debounceOut);
    let _submit = () => amountsOut(value);
    let fncDelayed = setTimeout(_submit, debounceTiming);
    setDebounceOut(fncDelayed);
  };
  const loadInit = () => {
    initSwap();
    dispatch(setTokenSend(tokenSend));
    dispatch(setTokenReceived(tokenReceived));
  };

  const initSwap = async () => {
    // swap configs
    let swaps = await InitializeSwaps();
    dispatch(InitializeSwapsRD(swaps));
  };
  const finalSwap = async () => {
    setLoading(true);

    // config contract
    let ContractPeriphery = PeripheryContract(provider, signer);
    ContractPeriphery.options.onlyOperation = true;

    // get functions
    let functions = ContractPeriphery.functions;
    let walletAddress = signer.getAddress();
    let _params = {
      path: routerSwap.map((route) => route.address),
      from: walletAddress,
      receiver: walletAddress,
    };
    let configs = {
      payer: walletAddress,
    };

    try {
      let account_rc = await provider.getAccountRc(walletAddress);
      let rc_limit = calculateDefaultMana(account_rc);
      configs.rc_limit = rc_limit;
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Error in mana calculation", { variant: "error" });
      return;
    }

    // amounts, the approval must cover the maximum the swap can spend
    let send = koilibUtils.parseUnits(maxDecimal(inputSend, tokenSend.decimals), tokenSend.decimals);
    let received = koilibUtils.parseUnits(maxDecimal(inputReceived, tokenReceived.decimals), tokenReceived.decimals);
    let amountOutMin = new BigNumber(received)
      .times(new BigNumber(slippage).div(100))
      .minus(received)
      .times(-1)
      .toFixed(0, 1);
    let amountInMax = new BigNumber(send).times(new BigNumber(slippage).div(100)).plus(send).toFixed(0, 1);
    let spendLimit = typeSwap === "SWAP-IN" ? send : amountInMax;

    // the quote may come from the KoinDX API, the route is checked on-chain before signing
    try {
      let routePairs = await fetchRoutePairs(routerSwap);
      let check =
        typeSwap === "SWAP-IN"
          ? quoteRoute(routePairs, routerSwap, send, { exactIn: true })
          : quoteRoute(routePairs, routerSwap, received, { exactIn: false });
      let priceMoved =
        !check ||
        (typeSwap === "SWAP-IN"
          ? check.amount_out_min.isLessThan(amountOutMin)
          : check.amount_in_max.isGreaterThan(amountInMax));
      if (priceMoved) {
        Snackbar.enqueueSnackbar("The price moved beyond your slippage, review the new quote", { variant: "warning" });
        pairsQuery.refetch();
        setLoading(false);
        return;
      }
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar("Could not check the price on-chain, try again", { variant: "error" });
      setLoading(false);
      return;
    }

    let operations = [];
    // add approval to operation
    try {
      if (_get(routerSwap[0], "allowance", true)) {
        let address_token = routerSwap[0].address;
        if (
          _get(CONFIG_BASE, "chain.nameservice", []).indexOf(address_token) != -1 &&
          _get(CONFIG_BASE, "chain.contracts.nameservice", "") != ""
        ) {
          let ns = NameServiceContract(provider, signer);
          let result = await ns.functions.get_address({ name: address_token });
          address_token = _get(result, "result.value.address", "");
        }
        let _token = TokenContract(address_token, provider, signer);
        _token.options.onlyOperation = true;
        const approvalParams = {
          owner: walletAddress,
          spender: ContractPeriphery.getId(),
          value: spendLimit,
        };
        let { operation: approveSales } = await _token.functions.approve(approvalParams, configs);
        operations.push(approveSales);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Approval error", { variant: "error" });
      return;
    }

    // swap tokens
    if (typeSwap === "SWAP-IN") {
      let params = {
        amountIn: send,
        amountOutMin: amountOutMin,
        ..._params,
      };
      try {
        let { operation: swapIn } = await functions.swap_tokens_in(params, configs);
        operations.push(swapIn);
      } catch (error) {
        console.log("ERROR SWAP-IN:", error);
        Snackbar.enqueueSnackbar("Error in swap in", { variant: "error" });
        setLoading(false);
        return;
      }
    } else {
      let params = {
        amountOut: received,
        amountInMax: amountInMax,
        ..._params,
      };
      try {
        let { operation: swapOut } = await functions.swap_tokens_out(params, configs);
        operations.push(swapOut);
      } catch (error) {
        console.log("ERROR SWAP-OUT:", error);
        Snackbar.enqueueSnackbar("Error in swap out", { variant: "error" });
        setLoading(false);
        return;
      }
    }
    let transaction = null;
    let receipt = null;
    try {
      const tx = await signer.prepareTransaction({ operations: operations, header: configs });
      const { transaction: _transaction, receipt: _receipt } = await signer.sendTransaction(tx);
      transaction = _transaction;
      receipt = _receipt;
      Snackbar.enqueueSnackbar("Transaction sent", { variant: "info" });
    } catch (error) {
      Snackbar.enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      console.log(error);
      setLoading(false);
      return;
    }

    if (receipt.logs) {
      // console.log(receipt.logs.join(", "))
    }
    // wait to be mined
    try {
      await waitTransation(provider, transaction);
    } catch (error) {
      console.log(error);
      const { message, variant } = describeWaitError(error);
      Snackbar.enqueueSnackbar(<TxSnackbarMessage message={message} txId={transaction.id} />, {
        variant,
        autoHideDuration: 15000,
      });
      setLoading(false);
      return;
    }

    Snackbar.enqueueSnackbar(<TxSnackbarMessage message="Transaction successful" txId={transaction.id} />, {
      variant: "success",
      autoHideDuration: 10000,
    });
    setLoading(false);
    cleanState();
    // balances and reserves changed
    invalidateAfterTransaction();
  };
  // functions component
  const disabledButton = () => {
    if (!tokenSend || !tokenReceived) return true;
    if (routerErrors) return true;
    if (loading) return true;
    let inputSendPrev = maxDecimal(inputSend != "" ? inputSend : "0", tokenSend.decimals);
    let _inputSend = koilibUtils.parseUnits(inputSendPrev, tokenSend.decimals);
    if (new BigNumber(_inputSend).isGreaterThan(balanceSend)) return true;
    if (!inputSend) return true;
    return false;
  };
  const messageButton = () => {
    if (!reserve) return "Loading reserves...";
    if (!tokenSend || !tokenReceived) return "Select a token";
    if (routerErrors === "Finite") return "Insufficient liquidity";
    if (routerErrors === "NotRoute") return "No route for this pair";
    if (routerErrors) return "Unable to quote this trade";
    if (loading) return "Loading";
    if (new BigNumber(inputSend).isNaN()) return "Enter an amount";
    let inputSendPrev = maxDecimal(inputSend != "" ? inputSend : "0", tokenSend.decimals);
    let _inputSend = koilibUtils.parseUnits(inputSendPrev, tokenSend.decimals);
    if (new BigNumber(_inputSend).isGreaterThan(balanceSend))
      return `Insufficient ${_get(tokenSend, "symbol", "")} balance`;
    return "Swap";
  };
  // minimum received (exact input) or maximum sold (exact output), in the units of that token
  const minAndMax = () => {
    let result = "0";
    if (tokenSend && tokenReceived) {
      if (typeSwap === "SWAP-IN") {
        let received = koilibUtils.parseUnits(
          maxDecimal(inputReceived != "" ? inputReceived : "0", tokenReceived.decimals),
          tokenReceived.decimals
        );
        let amount = new BigNumber(received)
          .times(new BigNumber(slippage).div(100))
          .minus(received)
          .times(-1)
          .toFixed(0, 1);
        result = tokenReceived.decimals != "0" ? koilibUtils.formatUnits(amount, tokenReceived.decimals) : amount;
      } else {
        let send = koilibUtils.parseUnits(
          maxDecimal(inputSend != "" ? inputSend : "0", tokenSend.decimals),
          tokenSend.decimals
        );
        let amount = new BigNumber(send).times(new BigNumber(slippage).div(100)).plus(send).toFixed(0, 1);
        result = tokenSend.decimals != "0" ? koilibUtils.formatUnits(amount, tokenSend.decimals) : amount;
      }
    }
    return result;
  };
  const limitSymbol = () => _get(typeSwap === "SWAP-IN" ? tokenReceived : tokenSend, "symbol", "");

  const PriceImpactWarning = () => {
    const impact = new BigNumber(priceImpact);
    const warningLevel = impact.isGreaterThanOrEqualTo("15")
      ? impact.isGreaterThanOrEqualTo("30")
        ? "error"
        : "warning"
      : "info";
    if (warningLevel !== "info") {
      return (
        <Alert variant="outlined" sx={{ mt: 2 }} severity={warningLevel}>
          High price impact: this swap moves the price about {impact.toString()}%.
        </Alert>
      );
    }
  };

  //efects
  useEffect(loadInit, []);
  useEffect(cleanState, [tokenSend, tokenReceived]);
  useEffect(changeReserves, [reserve]);

  if (!_get(CONFIG_BASE, "contracts.periphery.launched", false)) {
    return <Maintenance />;
  }

  // every swap is reviewed in the confirmation modal before signing
  const handleFinalSwap = () => {
    // each hop keeps 0.25% of its input for the liquidity providers
    const hops = Math.max(routerSwap.length - 1, 1);
    const lpFee = new BigNumber(inputSend).times(new BigNumber(1).minus(new BigNumber("0.9975").pow(hops)));
    dispatch(
      setModalData({
        onConfirm: finalSwap,
        tokenSend,
        tokenReceived,
        amountSend: formatAmount(inputSend, { maxDecimals: 8 }),
        amountReceived: formatAmount(inputReceived, { maxDecimals: 8 }),
        exactIn: typeSwap === "SWAP-IN",
        limit: formatAmount(minAndMax(), { maxDecimals: 8 }),
        route: routerSwap.map((token) => token.symbol),
        path: swapPath,
        priceImpact,
        slippage,
        lpFee: formatAmount(lpFee, { maxDecimals: 8 }),
      })
    );
    dispatch(setModal("ConfirmSwap"));
  };

  const hasWallet = Boolean(_get(walletSelector, "wallet", null));
  const showDetails =
    routerSwap.length && reserve && Object.keys(reserve).length && typeSwap && inputSend && inputReceived;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pb: { xs: 10, md: 6 } }}>
      <Card sx={{ maxWidth: 480, width: "100%" }}>
        <CardHeader
          title="Swap"
          action={
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Refresh prices">
                <span>
                  <IconButton
                    aria-label="refresh prices"
                    disabled={!tokenSend || !tokenReceived}
                    onClick={() => pairsQuery.refetch()}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Settings">
                <IconButton aria-label="settings" onClick={() => openModalSettingsApp()}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <CardContent>
          <TokenInputPanel
            label="You pay"
            token={tokenSend}
            inputVal={inputSend}
            disabledLoading={reserve == null}
            onSelect={() => openModalSelectToken({ reduce: "swap", value: "send" })}
            onBalance={(balance) => setBalanceSend(balance)}
            onInput={(amount) => debounceFnIn(amount)}
          />
          {/* the switch sits on the seam between both wells */}
          <Box sx={{ display: "flex", justifyContent: "center", my: "-18px", position: "relative", zIndex: 1 }}>
            <IconButton
              aria-label="switch tokens"
              onClick={() => swichTokens()}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                color: "text.primary",
                backgroundColor: "background.light",
                border: 4,
                borderColor: "background.paper",
                "&:hover": { backgroundColor: "secondary.dark", color: "primary.main" },
              }}
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Box>
          <TokenInputPanel
            label="You receive"
            token={tokenReceived}
            inputVal={inputReceived}
            disabledLoading={reserve == null}
            onSelect={() => openModalSelectToken({ reduce: "swap", value: "received" })}
            onBalance={(balance) => setBalanceReceived(balance)}
            onInput={(amount) => debounceFnOut(amount)}
          />

          {inputSend && inputReceived ? <PriceImpactWarning impactValue={priceImpact} /> : null}

          <Box sx={{ mt: 2 }}>
            {!hasWallet ? (
              <Button variant="contained" size="large" fullWidth onClick={() => dispatch(setModal("Connect"))}>
                Connect wallet
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => handleFinalSwap()}
                disabled={disabledButton()}
              >
                {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1.25 }} /> : null}
                {messageButton()}
              </Button>
            )}
          </Box>

          {showDetails ? (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <DetailRow
                label="Rate"
                value={
                  exchangeRate && exchangeRate != "0"
                    ? `1 ${tokenSend.symbol} = ${formatAmount(exchangeRate, { maxDecimals: 8 })} ${tokenReceived.symbol}`
                    : "-"
                }
              />
              <DetailRow label="Price impact" value={`${priceImpact.toString()}%`} />
              <DetailRow
                label={
                  <Tooltip
                    placement="top"
                    title="How much the price may move against you before the swap reverts. Change it in settings."
                  >
                    <span>Slippage tolerance</span>
                  </Tooltip>
                }
                value={`${slippage}%`}
              />
              <DetailRow
                label={typeSwap === "SWAP-IN" ? "Minimum received" : "Maximum sold"}
                value={`${formatAmount(minAndMax(), { maxDecimals: 8 })} ${limitSymbol()}`}
              />
              <SwapRoute path={swapPath} tokenSend={tokenSend} tokenReceived={tokenReceived} />
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SwapPage;
